import { useEffect, useId, type KeyboardEvent } from "react"
import { usePopover } from "../ds/usePopover"
import "./RowMenu.css"

export interface MenuItem {
  key: string
  label: string
  onSelect: () => void
  danger?: boolean
  /** Set for a toggle: the item is a checkbox menu item. */
  checked?: boolean
}

/** A small "more" button and its menu. Arrow keys move, Escape closes and returns focus to the button. */
export function RowMenu({ label, items, align = "right", className = "" }: {
  label: string; items: MenuItem[]; align?: "left" | "right"; className?: string
}) {
  const { open, setOpen, button, panel } = usePopover<HTMLButtonElement, HTMLDivElement>()
  const id = useId()
  useEffect(() => {
    if (open) panel.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus()
  }, [open, panel])
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const all = Array.from(panel.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])
    const at = all.indexOf(document.activeElement as HTMLElement)
    const to = e.key === "ArrowDown" ? (at + 1) % all.length : e.key === "ArrowUp" ? (at - 1 + all.length) % all.length : e.key === "Home" ? 0 : e.key === "End" ? all.length - 1 : -1
    if (to < 0) return
    e.preventDefault()
    all[to]?.focus()
  }
  return (
    <div className={`rm ${className}`} data-open={open || undefined}>
      <button ref={button} type="button" className="rm-btn" aria-label={label} aria-haspopup="menu" aria-expanded={open}
        aria-controls={open ? id : undefined} onClick={() => setOpen((v) => !v)}>
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor"><circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" /></svg>
      </button>
      {open ? (
        <div ref={panel} id={id} role="menu" aria-label={label} className={`rm-pop ${align}`} onKeyDown={onKey}>
          {items.map((it) => (
            <button key={it.key} type="button" role={it.checked === undefined ? "menuitem" : "menuitemcheckbox"}
              aria-checked={it.checked} className={`rm-i${it.danger ? " danger" : ""}`}
              onClick={() => { setOpen(false); it.onSelect() }}>
              <span>{it.label}</span>
              {it.checked !== undefined ? <span className="rm-on" aria-hidden="true">{it.checked ? "On" : "Off"}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
